(() => {
  'use strict';
  if (customElements.get('edu-shipping-estimate')) return;
  const instances = new Set();
  const storageKey = 'educatodo-shipping-region-v1';
  let region = '';
  let cart = null;
  let request = null;
  let revision = 0;
  try {
    const saved = localStorage.getItem(storageKey);
    if (['cdmx', 'rest', 'intl'].includes(saved)) region = saved;
  } catch (_) {}

  const renderAll = () => instances.forEach(instance => instance.render());
  const setCart = value => {
    if (!value || !Number.isFinite(value.total_price)) return;
    cart = value;
    revision += 1;
    renderAll();
  };
  const setRegion = value => {
    region = ['', 'cdmx', 'rest', 'intl'].includes(value) ? value : '';
    try {
      localStorage.setItem(storageKey, region);
    } catch (_) {}
    renderAll();
  };
  const refreshCart = () => {
    if (request) return request;
    const started = revision;
    const route = window.Shopify?.routes?.root || '/';
    request = fetch(`${route}cart.js`, {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store'})
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Cart unavailable')))
      .then(value => { if (revision === started) setCart(value); })
      .catch(() => {})
      .finally(() => { request = null; });
    return request;
  };

  class EducatodoShippingEstimate extends HTMLElement {
    static rate(amount, zone) {
      if (!Number.isFinite(amount) || amount < 0) return null;
      if (zone === 'cdmx') return amount >= 80000 ? 0 : 9500;
      if (zone !== 'rest' || amount < 4000) return null;
      if (amount >= 120000) return 0;
      if (amount >= 50000) return 12000;
      if (amount >= 20000) return 15000;
      return 16000;
    }

    connectedCallback() {
      if (this.bound) return;
      const configNode = this.querySelector('[data-edu-shipping-config]');
      if (!configNode) return;
      try { this.config = JSON.parse(configNode.textContent); } catch (_) { return; }
      this.bound = true;
      this.strings = this.config.strings;
      this.scope = this.closest('ed-bingo-product, .product--outer, [data-product-wrapper]') || this.parentElement;
      this.onChange = event => {
        if (event.target.matches('[data-edu-region]')) setRegion(event.target.value);
        else this.render();
      };
      this.onProductChange = () => requestAnimationFrame(() => this.render());
      this.addEventListener('change', this.onChange);
      if (this.dataset.context === 'product') {
        this.scope.addEventListener('change', this.onProductChange);
        this.scope.addEventListener('input', this.onProductChange);
        this.scope.addEventListener('click', this.onProductChange);
        this.scope.addEventListener('variant-change', this.onProductChange, true);
      }
      if (!cart) cart = this.config.cart;
      instances.add(this);
      this.render();
    }

    disconnectedCallback() {
      instances.delete(this);
      this.removeEventListener('change', this.onChange);
      if (this.scope && this.onProductChange) {
        this.scope.removeEventListener('change', this.onProductChange);
        this.scope.removeEventListener('input', this.onProductChange);
        this.scope.removeEventListener('click', this.onProductChange);
        this.scope.removeEventListener('variant-change', this.onProductChange, true);
      }
      this.bound = false;
    }

    money(cents) {
      return new Intl.NumberFormat((document.documentElement.lang || 'es').startsWith('es') ? 'es-MX' : document.documentElement.lang, {
        style: 'currency', currency: 'MXN', currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
      }).format(cents / 100);
    }

    text(key, amount) {
      return (this.strings[key] || '').replace('__AMOUNT__', this.money(amount));
    }

    productSelection() {
      const product = this.config.product;
      if (!product) return null;
      const form = this.dataset.productForm ? document.getElementById(this.dataset.productForm) : this.scope.querySelector('form[data-product-form], form[action*="/cart/add"]');
      const control = form?.querySelector('[name="id"]');
      const selectedId = control ? control.value : product.selectedVariantId;
      const variant = product.variants.find(value => String(value.id) === String(selectedId));
      const quantityInput = form?.querySelector('[name="quantity"]');
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);
      if (!variant || !Number.isInteger(quantity) || quantity < 1) return null;
      return {variant, quantity};
    }

    render() {
      if (!this.config || !cart) return;
      const isProduct = this.dataset.context === 'product';
      let amount = cart.total_price;
      let physical = cart.requires_shipping;
      let valid = true;
      if (isProduct) {
        const selection = this.productSelection();
        valid = Boolean(selection);
        physical = selection ? selection.variant.requires_shipping : true;
        if (selection) amount += selection.variant.price * selection.quantity;
      }
      this.hidden = !physical || (!isProduct && !cart.item_count);
      if (this.hidden) return;
      const selectedRegion = this.dataset.country !== 'MX' || (cart.currency || this.dataset.currency) !== 'MXN' ? 'intl' : region;
      const selector = this.querySelector('[data-edu-region]');
      if (selector) selector.value = selectedRegion;
      const domestic = this.querySelector('[data-edu-domestic]');
      const international = this.querySelector('[data-edu-international]');
      if (domestic) domestic.hidden = selectedRegion === 'intl';
      if (international) international.hidden = selectedRegion !== 'intl';
      const context = this.querySelector('[data-edu-context]');
      if (context) {
        context.textContent = !valid ? this.strings.invalidVariant : isProduct ? (cart.item_count ? this.strings.productCartContext : this.strings.productContext) : this.strings.estimatedNote;
      }
      this.querySelectorAll('[data-edu-zone]').forEach(zone => {
        const key = zone.dataset.eduZone;
        const threshold = key === 'cdmx' ? 80000 : 120000;
        const rate = EducatodoShippingEstimate.rate(amount, key);
        const remaining = Math.max(0, threshold - amount);
        zone.hidden = Boolean(selectedRegion && selectedRegion !== key) || !valid;
        const rateNode = zone.querySelector('[data-edu-rate]');
        if (rateNode) rateNode.textContent = rate === null ? this.strings.noRate : rate === 0 ? this.strings.freeRate : this.text('shippingRate', rate);
        const thresholdNode = zone.querySelector('[data-edu-threshold]');
        if (thresholdNode) thresholdNode.textContent = this.text('threshold', threshold);
        const remainingNode = zone.querySelector('[data-edu-remaining]');
        if (remainingNode) remainingNode.textContent = remaining ? this.text('remaining', remaining) : this.strings.free;
        zone.classList.toggle('is-complete', rate === 0);
        const progress = zone.querySelector('[data-edu-progress]');
        const fill = zone.querySelector('[data-edu-fill]');
        const percent = Math.min(100, Math.max(0, amount / threshold * 100));
        if (progress) {
          progress.setAttribute('aria-valuenow', String(Math.round(percent)));
          progress.setAttribute('aria-valuetext', remaining ? this.text('remaining', remaining) : this.strings.free);
        }
        if (fill) fill.style.width = `${percent}%`;
      });
      const summary = this.querySelector('[data-edu-summary]');
      const rate = EducatodoShippingEstimate.rate(amount, selectedRegion);
      if (summary) {
        summary.hidden = isProduct || !valid || !['cdmx', 'rest'].includes(selectedRegion) || rate === null;
        const total = summary.querySelector('[data-edu-total]');
        if (total && !summary.hidden) total.textContent = `${this.money(amount + rate)} MXN`;
      }
    }
  }

  customElements.define('edu-shipping-estimate', EducatodoShippingEstimate);
  window.addEventListener('cartcount:update', event => setCart(event.detail));
  window.addEventListener('pageshow', refreshCart);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCart(); });
  window.addEventListener('storage', event => {
    if (event.key === storageKey) {
      region = ['', 'cdmx', 'rest', 'intl'].includes(event.newValue) ? event.newValue : '';
      renderAll();
    }
  });
  document.addEventListener('change', event => {
    if (!['address_country', 'address_province'].includes(event.target.id)) return;
    requestAnimationFrame(() => {
      const country = document.getElementById('address_country');
      const province = document.getElementById('address_province');
      if (!country || !province) return;
      if (!['Mexico', 'México', 'MX'].includes(country.value)) setRegion('intl');
      else if (!province.value) setRegion('');
      else setRegion(/^(Ciudad de M[eé]xico|Distrito Federal|DF|CMX)$/i.test(province.value) ? 'cdmx' : 'rest');
    });
  });
  refreshCart();
})();
